import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, EmptyState, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, PageHeader } from "../ui/Layout";
import { api, ApiFehler, rufe } from "../api/client";
import { LadenAnlegen } from "@shared/schemas/laden";
import { formatDatum, formatTag } from "@shared/zeit";
import { aboStatusText } from "@shared/schemas/anbieter";

interface LadenZeile {
  id: string;
  name: string;
  slug: string;
  status: "test" | "aktiv" | "gesperrt";
  tarif: string;
  aboStatus: "test" | "aktiv" | "verzug" | "gekuendigt" | "frei";
  testphaseBis: string | null;
  aboBis: string | null;
  supportLetzterAt: string | null;
  sumupVerbundenAt: string | null;
  erstelltAt: string;
  regale: number;
  mieter: number;
}

function useLaeden() {
  return useQuery({ queryKey: ["admin", "laeden"], queryFn: () => rufe<{ laeden: LadenZeile[] }>(api.api.admin.laeden.$get()) });
}

export function Laeden() {
  const laeden = useLaeden();
  const qc = useQueryClient();
  const [offen, setOffen] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const einladung = useMutation({
    mutationFn: (id: string) => rufe(api.api.admin.laeden[":id"].einladung.$post({ param: { id } })),
    onSuccess: () => setHinweis("Einladung erneut verschickt (siehe Dev-Postfach)."),
  });
  const status = useMutation({
    mutationFn: (p: { id: string; status: "aktiv" | "gesperrt" }) => rufe(api.api.admin.laeden[":id"].status.$post({ param: { id: p.id }, json: { status: p.status } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "laeden"] }),
  });
  const abo = useMutation({
    mutationFn: (p: { id: string; aboStatus: "frei" | "test"; testTage?: number }) => rufe(api.api.admin.laeden[":id"].abo.$post({ param: { id: p.id }, json: { aboStatus: p.aboStatus, testTage: p.testTage } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "laeden"] }),
  });
  const support = useMutation({
    mutationFn: (id: string) => rufe<{ bis: string; weiter: string }>(api.api.admin.laeden[":id"].support.$post({ param: { id }, json: { stunden: 2 } })),
    onSuccess: (r) => {
      qc.clear();
      window.location.assign(r.weiter);
    },
  });

  return (
    <>
      <PageHeader title="Läden" sub="Alle Läden, Status und Einladungen" actions={<Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Laden anlegen</Button>} />
      {hinweis ? <Notice tone="success">{hinweis}</Notice> : null}
      <Seitenzustand
        query={laeden}
        istLeer={(d) => d.laeden.length === 0}
        leer={
          <EmptyState illustration="empty-shelf" title="Noch kein Laden" action={<Button variant="primary" icon="plus" onClick={() => setOffen(true)}>Ersten Laden anlegen</Button>}>
            Lege den ersten Laden an. Der Inhaber bekommt einen Einladungslink und richtet den Laden selbst ein.
          </EmptyState>
        }
      >
        {(d) => (
          <Card flush>
            <div className="sc-scroll">
              <table className="rg-table">
                <thead>
                  <tr>
                    <th>Laden</th>
                    <th>Status</th>
                    <th>Abo</th>
                    <th className="is-num">Regale</th>
                    <th className="is-num">Mieter</th>
                    <th>SumUp</th>
                    <th>Seit</th>
                    <th>
                      <span className="rg-sr">Aktionen</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.laeden.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <span className="sc-strong">{l.name}</span>
                        <div className="sc-muted sc-small">/{l.slug}</div>
                      </td>
                      <td>{l.status === "gesperrt" ? <StatusBadge status="error">Gesperrt</StatusBadge> : l.status === "test" ? <StatusBadge status="wait">Test</StatusBadge> : <StatusBadge status="paid">Aktiv</StatusBadge>}</td>
                      <td>
                        <StatusBadge status={l.aboStatus === "verzug" ? "error" : l.aboStatus === "test" ? "wait" : l.aboStatus === "gekuendigt" ? "neutral" : "paid"}>{aboStatusText(l.aboStatus)}</StatusBadge>
                        {l.testphaseBis ? <div className="sc-muted sc-small">bis {formatTag(l.testphaseBis)}</div> : null}
                      </td>
                      <td className="is-num">{l.regale}</td>
                      <td className="is-num">{l.mieter}</td>
                      <td>{l.sumupVerbundenAt ? <StatusBadge status="paid">verbunden</StatusBadge> : <StatusBadge status="neutral">noch nicht verbunden</StatusBadge>}</td>
                      <td className="sc-num">{formatDatum(l.erstelltAt)}</td>
                      <td>
                        <div className="sc-toolbar">
                          <Button variant="quiet" icon="mail" onClick={() => einladung.mutate(l.id)} loading={einladung.isPending && einladung.variables === l.id}>
                            Einladung senden
                          </Button>
                          {l.status === "gesperrt" ? (
                            <Button variant="quiet" icon="circle-check" onClick={() => status.mutate({ id: l.id, status: "aktiv" })}>
                              Freigeben
                            </Button>
                          ) : (
                            <Button variant="quiet" icon="lock" onClick={() => status.mutate({ id: l.id, status: "gesperrt" })}>
                              Sperren
                            </Button>
                          )}
                          {l.aboStatus !== "frei" ? (
                            <Button variant="quiet" icon="sparkles" onClick={() => abo.mutate({ id: l.id, aboStatus: "frei" })}>
                              Kostenlos stellen
                            </Button>
                          ) : null}
                          {l.aboStatus === "test" || l.aboStatus === "frei" ? (
                            <Button variant="quiet" icon="clock" onClick={() => abo.mutate({ id: l.id, aboStatus: "test", testTage: 14 })}>
                              Testphase 14 Tage
                            </Button>
                          ) : null}
                          <Button variant="quiet" icon="shield-check" onClick={() => { if (window.confirm(`Support-Zugang für ${l.name} öffnen? Die Sitzung gilt 2 Stunden, wird im Protokoll des Ladens vermerkt und ersetzt deine Admin-Anmeldung.`)) support.mutate(l.id); }} loading={support.isPending && support.variables === l.id}>
                            Support-Zugang
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </Seitenzustand>
      <LadenAnlegenDialog offen={offen} onClose={() => setOffen(false)} />
      <TotpReset />
    </>
  );
}

/** Support: Zwei-Faktor einer Person zurücksetzen (Handy verloren). Alle Sitzungen enden; Inhaber müssen neu einrichten. */
function TotpReset() {
  const [email, setEmail] = useState("");
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const reset = useMutation({
    mutationFn: (e: string) => rufe(api.api.admin.benutzer["totp-zuruecksetzen"].$post({ json: { email: e } })),
    onSuccess: () => { setMeldung({ tone: "success", text: "Zwei-Faktor zurückgesetzt, alle Sitzungen beendet. Die Person meldet sich per E-Mail-Link neu an und richtet den zweiten Faktor neu ein." }); setEmail(""); },
    onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Das hat nicht geklappt." }),
  });
  return (
    <Card title="Support: Zwei-Faktor zurücksetzen" className="app-form">
      <p className="sc-muted">Nur nach Prüfung der Identität (Rückruf unter der bekannten Telefonnummer). Danach gilt für Inhaber wieder die Einrichtungspflicht.</p>
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
      <div className="sc-form" style={{ alignItems: "end" }}>
        <TextField label="E-Mail-Adresse der Person" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button variant="danger" icon="lock" loading={reset.isPending} disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())} onClick={() => confirm(`Zwei-Faktor für ${email.trim()} zurücksetzen?`) && reset.mutate(email.trim().toLowerCase())}>Zurücksetzen</Button>
      </div>
    </Card>
  );
}

function LadenAnlegenDialog({ offen, onClose }: { offen: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", slug: "", inhaberName: "", inhaberEmail: "" });
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const anlegen = useMutation({
    mutationFn: (daten: LadenAnlegen) => rufe<{ ladenId: string }>(api.api.admin.laeden.$post({ json: daten })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "laeden"] });
      setForm({ name: "", slug: "", inhaberName: "", inhaberEmail: "" });
      onClose();
    },
    onError: (err) => setFehler({ _: err instanceof ApiFehler ? err.message : "Anlegen hat nicht geklappt." }),
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "name" && !f.slug ? {} : {}) }));
  };
  const slugVorschlag = form.slug || form.name.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler({});
    const parsed = LadenAnlegen.safeParse({ ...form, slug: slugVorschlag });
    if (!parsed.success) {
      const f: Record<string, string> = {};
      for (const i of parsed.error.issues) f[String(i.path[0])] = i.message;
      setFehler(f);
      return;
    }
    anlegen.mutate(parsed.data);
  }

  return (
    <Dialog
      open={offen}
      title="Laden anlegen"
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" icon="mail" type="submit" form="laden-anlegen" loading={anlegen.isPending}>
            Anlegen und einladen
          </Button>
        </>
      }
    >
      <form id="laden-anlegen" className="app-form" onSubmit={absenden} noValidate>
        {fehler._ ? <Notice tone="error">{fehler._}</Notice> : null}
        <TextField label="Name des Ladens" placeholder="z. B. Regalglück Eutin" value={form.name} onChange={set("name")} error={fehler.name} autoFocus />
        <TextField label="Kurzname (Adresse)" placeholder="regalglueck-eutin" value={slugVorschlag} onChange={set("slug")} error={fehler.slug} hint="Nur Kleinbuchstaben, Ziffern und Bindestriche. Teil der Login-Adresse des Mieterportals." />
        <TextField label="Name des Inhabers" value={form.inhaberName} onChange={set("inhaberName")} error={fehler.inhaberName} />
        <TextField label="E-Mail des Inhabers" type="email" value={form.inhaberEmail} onChange={set("inhaberEmail")} error={fehler.inhaberEmail} hint="Bekommt den Einladungslink (15 Minuten gültig)." />
      </form>
    </Dialog>
  );
}
